import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bitcoin, Coins, TrendingUp, TrendingDown, Info, Zap } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useMarketMetrics } from '@/hooks/useMarketMetrics';
import { useHyperliquidData } from '@/hooks/useHyperliquidData';
import { cn } from '@/lib/utils';
import { formatPrice, formatPercent, formatUSD, formatRatio } from '@/lib/format';
import { Skeleton } from '@/components/ui/skeleton';

interface AssetRowProps {
  asset: 'BTC' | 'ETH';
}

export const AssetRow = ({ asset }: AssetRowProps) => {
  const { data: metrics, isLoading: metricsLoading } = useMarketMetrics(asset);
  const { data: hyperliquid } = useHyperliquidData(asset);
  
  const isFresh = (timestamp: string) => {
    if (!timestamp) return false;
    const age = Date.now() - Date.parse(timestamp);
    return age < 2 * 60 * 1000; // < 2 minutos
  };
  
  const Icon = asset === 'BTC' ? Bitcoin : Coins;
  const iconColor = asset === 'BTC' ? 'text-[#F7931A]' : 'text-[#627EEA]';
  
  if (metricsLoading) {
    return (
      <Card className="border-border/60 bg-card/40 backdrop-blur-sm hover:border-primary/40 transition-colors">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }
  
  if (!metrics) return null;

  const isExtremeRatio = metrics.topTraderRatio > 2.5 || metrics.topTraderRatio < 0.75;
  
  return (
    <Card className="border-border/60 bg-card/40 backdrop-blur-sm hover:border-primary/40 transition-colors">
      <CardContent className="p-6">
        {/* Header: Ícone, Nome e Preço Principal */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-border/50">
          <div className="flex items-center gap-4">
            <div className={cn("p-3 rounded-full bg-muted/50", iconColor)}>
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <h2 className="font-bold text-2xl tracking-tight">{asset}</h2>
              <p className="text-xs text-muted-foreground">
                {new Date(metrics.timestamp).toLocaleTimeString('pt-BR')}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold font-mono tracking-tight">
              {formatPrice(metrics.price)}
            </div>
            <div className={cn(
              "text-xl font-bold font-mono",
              metrics.px24h >= 0 ? 'text-profit' : 'text-loss'
            )}>
              {formatPercent(metrics.px24h, 2)}
            </div>
          </div>
        </div>

        {/* Grid de Métricas Compactas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {/* Funding Rate */}
          <div className="rounded-lg border border-border/50 bg-background/50 p-3">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
              Funding
            </p>
            <p className={cn(
              "text-sm font-bold font-mono",
              metrics.funding24h >= 0 ? 'text-profit' : 'text-loss'
            )}>
              {(metrics.funding24h).toFixed(4)}%
            </p>
          </div>

          {/* OI Change */}
          <div className="rounded-lg border border-border/50 bg-background/50 p-3">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
              OI Δ24h
            </p>
            <p className={cn(
              "text-sm font-bold font-mono",
              metrics.oi24h >= 0 ? 'text-profit' : 'text-loss'
            )}>
              {formatUSD(metrics.oi24h, 0)}
            </p>
          </div>

          {/* Top Trader Ratio */}
          <div className="rounded-lg border border-border/50 bg-background/50 p-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Top Ratio
              </p>
              {isExtremeRatio && (
                <Badge variant="destructive" className="text-[8px] h-4 px-1">
                  EXT
                </Badge>
              )}
            </div>
            <p className="text-sm font-bold font-mono">
              {formatRatio(metrics.topTraderRatio)}
            </p>
          </div>

          {/* Liquidations */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="rounded-lg border border-border/50 bg-background/50 p-3 cursor-pointer">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
                    Liq. 24h
                  </p>
                  <p className="text-sm font-bold font-mono text-loss">
                    {formatUSD(metrics.liquidations24h, 1)}
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{metrics.liquidations24hCount || 0} ordens liquidadas</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Data Origin Badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          {/* Market Data Source Badge */}
          {metrics.source && (
            <Badge variant="default" className="text-xs bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
              📊 {metrics.source} {isFresh(metrics.timestamp) ? '🟢' : '⚠️'}
            </Badge>
          )}
          
          {/* Hyperliquid Smart Money Badge */}
          {hyperliquid && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-xs gap-1 bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                    <Zap className="h-3 w-3" />
                    Hyperliquid ({hyperliquid.smartMoneyMetrics.activeTraders})
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="space-y-1 text-xs">
                    <p>Long: {hyperliquid.smartMoneyMetrics.longTraders} | Short: {hyperliquid.smartMoneyMetrics.shortTraders}</p>
                    <p>Net Flow 7d: {formatUSD(hyperliquid.smartMoneyMetrics.netFlow7d)}</p>
                    <p>Exposure: {formatUSD(hyperliquid.smartMoneyMetrics.totalExposureUsd)}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          
          {/* Liquidations Badge */}
          {metrics.liquidations24h > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="destructive" className="text-xs">
                    💥 {metrics.liquidations24hCount} liquidações
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Total: {formatUSD(metrics.liquidations24h)}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Smart Money Section - Hyperliquid */}
        {hyperliquid && (
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-blue-500" />
                <span className="text-xs font-semibold text-blue-500">
                  Smart Money (Hyperliquid) • 7 dias
                </span>
              </div>
              <Badge variant="outline" className="text-[10px] h-5">
                {hyperliquid.smartMoneyMetrics.activeTraders}/{hyperliquid.smartMoneyMetrics.totalTraders} ativos
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Long/Short */}
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
                  Long / Short
                </p>
                <p className="text-sm font-bold font-mono">
                  <span className="text-profit">{hyperliquid.smartMoneyMetrics.longTraders}</span>
                  {' / '}
                  <span className="text-loss">{hyperliquid.smartMoneyMetrics.shortTraders}</span>
                </p>
              </div>

              {/* Bias */}
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
                  Position Bias
                </p>
                <p className={cn(
                  "text-sm font-bold font-mono",
                  hyperliquid.smartMoneyMetrics.netPositionBias >= 0 ? 'text-profit' : 'text-loss'
                )}>
                  {(hyperliquid.smartMoneyMetrics.netPositionBias * 100).toFixed(1)}%
                </p>
              </div>

              {/* Net Flow */}
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
                  Net Flow
                </p>
                <p className={cn(
                  "text-sm font-bold font-mono",
                  hyperliquid.smartMoneyMetrics.netFlow7d >= 0 ? 'text-profit' : 'text-loss'
                )}>
                  {formatUSD(hyperliquid.smartMoneyMetrics.netFlow7d, 1)}
                </p>
              </div>

              {/* Leverage */}
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
                  Alavancagem
                </p>
                <p className="text-sm font-bold font-mono">
                  {hyperliquid.smartMoneyMetrics.avgLeverage.toFixed(1)}x
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Footer com timestamp */}
        <div className="mt-4 pt-3 border-t border-border/50">
          <p className="text-[10px] text-muted-foreground text-center">
            Atualizado às {new Date(metrics.timestamp).toLocaleString('pt-BR', { 
              hour: '2-digit', 
              minute: '2-digit',
              second: '2-digit'
            })}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
